import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  Sheet,
  SheetContent,
} from './ui/sheet';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { toast } from 'sonner';
import {
  Building, MapPin, Home, Maximize, Compass, Layers,
  DollarSign, Wind, Coffee, Users as UsersIcon, Droplet, Shield,
  Navigation, Map, Flag, CheckCircle, X, ChevronLeft, ChevronRight
} from 'lucide-react';
import { addNote as createNote, getLeads, getNotes, updatePropertyStatus } from '../lib/apiService';
import { MAIN_API_BASE } from '../lib/apiConfig';
import Select from './ui/select';
import RentManagement from './RentManagement';

const ICON_MAP = {
  'home': Home,
  'tag': DollarSign,
  'maximize': Maximize,
  'compass': Compass,
  'layers': Layers,
  'dollar-sign': DollarSign,
  'wind': Wind,
  'coffee': Coffee,
  'users': UsersIcon,
  'droplet': Droplet,
  'shield': Shield,
  'map-pin': MapPin,
  'navigation': Navigation,
  'map': Map,
  'flag': Flag,
};

const InfoRow = ({ icon, label, value, hideBorder }) => {
  const Icon = ICON_MAP[icon] || Home;
  return (
    <div className={`flex items-center gap-3 py-3 ${!hideBorder ? 'border-b border-slate-100' : ''}`}>
      <div className="w-8 h-8 rounded-lg bg-orange-50 flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4 text-orange-500" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-400 uppercase tracking-wide">{label}</p>
        <p className="text-sm font-medium text-slate-800 mt-0.5">{typeof value === 'string' || typeof value === 'number' ? value : value}</p>
      </div>
    </div>
  );
};

const resolveImageUrl = (img, folder = 'property_images') => {
  if (!img || typeof img !== 'string') return '';
  const trimmed = img.trim();
  if (!trimmed || trimmed === 'NULL' || trimmed === 'null') return '';

  // Extract the filename regardless of host (192.168.x.x, localhost, or live)
  const filename = trimmed.split('?')[0].split('#')[0].split('/').pop();
  if (!filename) return '';

  return `${MAIN_API_BASE}/storage/${folder}/${filename}`;
};

const formatPrice = (price, isRent) => {
  const p = Number(price) || 0;
  if (!isRent) {
    if (p >= 10000000) return `₹${(p / 10000000).toFixed(2)} Cr`;
    if (p >= 100000) return `₹${(p / 100000).toFixed(2)} L`;
    return `₹${p.toLocaleString('en-IN')}`;
  }
  return `₹${p.toLocaleString('en-IN')}`;
};

const getRoomsList = (property) => {
  if (!property) return [];
  if (Array.isArray(property.rooms)) return property.rooms;
  if (typeof property.rooms === 'string') {
    try { return JSON.parse(property.rooms) || []; } catch (e) { return []; }
  }
  return [];
};

const getFoodImages = (property) => {
  if (!property) return [];
  if (Array.isArray(property.food_menu_images)) return property.food_menu_images;
  if (typeof property.food_menu_images === 'string') {
    try { 
      const parsed = JSON.parse(property.food_menu_images);
      if (Array.isArray(parsed)) return parsed;
      return [property.food_menu_images];
    } catch (e) { 
      if (property.food_menu_images.includes(',')) {
        return property.food_menu_images.split(',').map(s => s.trim());
      }
      return [property.food_menu_images]; 
    }
  }
  return [];
};

const PropertyDetail = ({ property, mode = 'details', onClose, onRefresh, allProperties = [], propertyIds = [], onPropertyChange }) => {
  const { userProfile } = useAuth();
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [status, setStatus] = useState(property?.status || 'active');
  const [availableLeads, setAvailableLeads] = useState([]);
  const [selectedLeadId, setSelectedLeadId] = useState(property?.sold_to_lead_id || '');
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  const isDetailsMode = mode === 'details';
  const isNotesMode = mode === 'notes';
  const isStatusMode = mode === 'status';

  const isRent = property?.listing_type === 'rent';
  const isPG = ['PG', 'Hostel'].includes(property?.property_type);
  const roomsList = getRoomsList(property);
  const foodMenuImagesList = getFoodImages(property);
  const images = property?.images && property.images.length > 0 ? property.images : [];

  useEffect(() => {
    setActiveImageIndex(0);
    setStatus(property?.status || 'active');
  }, [property?.id, property?.status]);

  const fetchNotes = useCallback(async () => {
    if (!isNotesMode || !property?.id) return;
    try {
      const response = await getNotes({ propertyId: property.id });
      setNotes(response || []);
    } catch (error) {
      console.error('Failed to fetch notes:', error);
    }
  }, [isNotesMode, property?.id]);

  const fetchPropertyLeads = useCallback(async () => {
    if (!isStatusMode || !userProfile || !property?.id) return;
    try {
      const response = await getLeads({
        currentUser: userProfile,
        page: 1,
        limit: 500
      });
      const leads = (response.leads || []).filter((lead) => {
        const ids = Array.isArray(lead.property_ids) ? lead.property_ids.map(String) : [String(lead.property_id || '')];
        return ids.includes(String(property.id));
      });
      setAvailableLeads(leads);
    } catch (error) {
      console.error('Failed to fetch property leads:', error);
    }
  }, [isStatusMode, property?.id, userProfile]);

  useEffect(() => {
    fetchNotes();
    fetchPropertyLeads();
  }, [fetchNotes, fetchPropertyLeads]);

  const addNote = async () => {
    if (!newNote.trim() || !userProfile || !property?.id) return;
    try {
      await createNote({
        propertyId: property.id,
        note: newNote,
        currentUser: userProfile
      });
      toast.success('Note added');
      setNewNote('');
      fetchNotes();
      onRefresh?.();
    } catch (error) {
      toast.error('Failed to add note');
    }
  };

  const handleStatusUpdate = async () => {
    if (!status || !property?.id) return;
    if (status === 'sold' && !selectedLeadId) {
      toast.error('Select a lead before marking this property as sold');
      return;
    }
    setIsUpdatingStatus(true);
    try {
      await updatePropertyStatus(property.id, status, {
        leadId: status === 'sold' ? selectedLeadId : undefined
      });
      toast.success(status === 'sold' ? 'Property sold' : 'Status updated');
      onRefresh?.();
      onClose?.();
    } catch (error) {
      toast.error(error.message || 'Failed to update status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  if (!property) return null;

  return (
    <Sheet open={true} onOpenChange={onClose}>
      <SheetContent className="bg-slate-50 sm:max-w-[480px] w-full overflow-y-auto p-0" data-testid="property-detail-panel">
        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-5 py-4 flex items-center justify-between">
          <div>
            <h2 className="text-base font-semibold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {isDetailsMode ? 'Property Details' : isNotesMode ? 'Property Notes' : 'Update Status'}
            </h2>
            <p className="text-xs text-slate-500">ID: {property.id}</p>
          </div>
          <button onClick={onClose} className="w-8 h-8 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center transition">
            <X className="w-4 h-4 text-slate-500" />
          </button>
        </div>

        <div className="p-5">
          {/* Property selector dropdown (if multiple properties provided) */}
          {propertyIds.length > 1 && (
            <div className="px-1 mb-4">
              <label className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2 block">Select Property</label>
              <Select
                value={String(property.id)}
                onChange={(val) => {
                  const newProp = allProperties.find(p => String(p.id) === String(val));
                  if (newProp && onPropertyChange) onPropertyChange(newProp);
                }}
                options={allProperties.map(p => ({
                  value: String(p.id),
                  label: `${p.property_title || p.title || `Property ${p.id}`} (${p.property_type || 'Property'})`
                }))}
              />
            </div>
          )}

          {isDetailsMode && (
            <div className="space-y-4">
              {/* Image Carousel */}
              {images.length > 0 ? (
                <div className="relative rounded-xl overflow-hidden bg-slate-100 shadow-sm">
                  <img
                    src={resolveImageUrl(images[activeImageIndex], 'property_images')}
                    alt={property.property_title}
                    className="w-full h-56 object-cover"
                    onError={(e) => {
                      e.target.onerror = null;
                      e.target.src = 'https://via.placeholder.com/800x500/e2e8f0/94a3b8?text=No+Image';
                    }}
                  />
                  {/* Listing tag */}
                  <div className={`absolute top-3 left-3 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide text-white ${isRent ? 'bg-cyan-600' : 'bg-orange-500'}`}>
                    {isRent ? 'FOR RENT' : 'FOR SALE'}
                  </div>
                  {/* Status badge */}
                  <div className={`absolute top-3 right-3 px-3 py-1 rounded-full text-[11px] font-bold uppercase tracking-wide text-white ${
                    property.status === 'active' ? 'bg-emerald-500' :
                    property.status === 'sold' ? 'bg-red-500' :
                    property.status === 'rented' ? 'bg-blue-500' : 'bg-slate-500'
                  }`}>
                    {property.status}
                  </div>
                  {/* Image count */}
                  <div className="absolute bottom-3 right-3 bg-black/60 text-white text-xs px-2.5 py-1 rounded-full flex items-center gap-1">
                    📷 {activeImageIndex + 1}/{images.length}
                  </div>
                  {/* Navigation arrows */}
                  {images.length > 1 && (
                    <>
                      <button onClick={() => setActiveImageIndex(i => (i - 1 + images.length) % images.length)} className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 text-white rounded-full flex items-center justify-center hover:bg-black/60 transition">
                        <ChevronLeft className="w-4 h-4" />
                      </button>
                      <button onClick={() => setActiveImageIndex(i => (i + 1) % images.length)} className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/40 text-white rounded-full flex items-center justify-center hover:bg-black/60 transition">
                        <ChevronRight className="w-4 h-4" />
                      </button>
                    </>
                  )}
                  {/* Dots */}
                  {images.length > 1 && (
                    <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5">
                      {images.map((_, i) => (
                        <div key={i} className={`w-2 h-2 rounded-full transition-all ${i === activeImageIndex ? 'bg-orange-500 w-5' : 'bg-white/50'}`} />
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div className="rounded-xl bg-slate-100 h-44 flex flex-col items-center justify-center text-slate-400">
                  <Building className="w-10 h-10 mb-2 text-slate-300" />
                  <p className="text-xs">No photos uploaded</p>
                </div>
              )}

              {/* Title Section */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <h3 className="text-lg font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
                    {property.property_title || property.title}
                  </h3>
                  {property.property_type && (
                    <span className="shrink-0 text-xs font-semibold bg-orange-50 text-orange-600 border border-orange-200 px-2.5 py-1 rounded-full">
                      {property.property_type}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <span className="text-xl font-bold text-slate-900">{formatPrice(property.price, isRent)}</span>
                  {isRent && <span className="text-sm text-slate-400">/month</span>}
                  {property.price_type && <span className="text-xs text-orange-500 font-medium bg-orange-50 px-2 py-0.5 rounded-full">{property.price_type}</span>}
                </div>
              </div>

              {/* Location Row */}
              <div className="flex items-start gap-2 bg-orange-50/50 p-3.5 rounded-xl border border-orange-100">
                <MapPin className="w-4 h-4 text-orange-500 mt-0.5 shrink-0" />
                <p className="text-sm text-slate-600 leading-relaxed">
                  {[property.address, property.landmark, property.city, property.district, property.state].filter(Boolean).join(', ')}
                </p>
              </div>

              {/* Property Details Card */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <h4 className="text-sm font-bold text-slate-900 mb-3 uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>Property Details</h4>
                {[
                  { icon: 'home', label: 'Property Type', value: property.property_type },
                  { icon: 'home', label: 'Building / Society', value: property.building_name || null },
                  { icon: 'tag', label: 'Usage Type', value: !isPG ? property.usage_type : null },
                  { icon: 'maximize', label: 'Area', value: !isPG && property.area_sqft ? `${property.area_sqft} sqft` : null },
                  { icon: 'compass', label: 'Facing', value: !isPG ? property.facing : null },
                  { icon: 'layers', label: 'Furnishing', value: !isPG ? property.furnishing : null },
                  { icon: 'dollar-sign', label: 'Price Type', value: !isPG ? property.price_type : null },
                  {
                    icon: 'wind',
                    label: 'Room Types',
                    value: isPG ? (
                      roomsList.length > 0 ? (() => {
                        const hasAc = roomsList.some(r => Number(r.ac) === 1);
                        const hasNonAc = roomsList.some(r => Number(r.ac) === 0);
                        return (hasAc && hasNonAc) ? 'AC / Non-AC' : hasAc ? 'AC' : 'Non-AC';
                      })() : 'AC / Non-AC'
                    ) : null
                  },
                  {
                    icon: 'coffee',
                    label: 'Fooding Available',
                    value: isPG ? (Number(property.serves_food) === 1 ? 'Yes' : 'No') : null
                  },
                  {
                    icon: 'users',
                    label: 'Occupancy',
                    value: isPG && roomsList.length > 0 ? (() => {
                      const types = Array.from(new Set(roomsList.map(r => r.room_type || r.roomType))).filter(Boolean);
                      return types.length > 0 ? types.join(' / ') : null;
                    })() : null
                  },
                  {
                    icon: 'droplet',
                    label: 'Attached Washroom',
                    value: isPG && roomsList.length > 0 ? (() => {
                      const hasWashroom = roomsList.some(r => Number(r.attached_washroom || r.attachedWashroom) === 1);
                      const hasNoWashroom = roomsList.some(r => Number(r.attached_washroom || r.attachedWashroom) === 0);
                      return (hasWashroom && hasNoWashroom) ? 'Yes / No (Varies by room)' : hasWashroom ? 'Yes' : 'No';
                    })() : null
                  },
                  {
                    icon: 'shield',
                    label: 'Security Deposit',
                    value: isRent && property.security_deposit ? `₹${Number(property.security_deposit).toLocaleString('en-IN')}` : null
                  },
                ]
                  .filter(item => item.value)
                  .map((item, index, array) => (
                    <InfoRow key={index} icon={item.icon} label={item.label} value={item.value} hideBorder={index === array.length - 1} />
                  ))}
              </div>

              {/* Available Rooms (PG/Hostel) */}
              {isPG && roomsList.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                  <h4 className="text-sm font-bold text-slate-900 mb-3 uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>Available Rooms</h4>
                  {roomsList.map((room, idx) => (
                    <div key={idx} className={`py-3 ${idx !== roomsList.length - 1 ? 'border-b border-slate-100' : ''}`}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-semibold text-slate-800">{room.room_type || room.roomType || 'Room'} Room</span>
                        <span className="text-sm font-bold text-orange-600">₹{Number(room.price_per_bed || room.pricePerBed || 0).toLocaleString('en-IN')}/month</span>
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        <span className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full font-medium">
                          {Number(room.ac) === 1 ? '❄️ AC' : '💨 Non-AC'}
                        </span>
                        <span className="text-xs bg-slate-100 text-slate-600 px-2.5 py-1 rounded-full font-medium">
                          {Number(room.attached_washroom || room.attachedWashroom) === 1 ? '🚿 Attached Bath' : '🚿 Shared Bath'}
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Description */}
              {property.description && (
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                  <h4 className="text-sm font-bold text-slate-900 mb-3 uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>Description</h4>
                  <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{property.description}</p>
                </div>
              )}

              {/* Amenities */}
              {property.amenities && (
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                  <h4 className="text-sm font-bold text-slate-900 mb-3 uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>Amenities</h4>
                  <div className="flex flex-wrap gap-2">
                    {property.amenities.split(',').map((item, i) => (
                      <span key={i} className="inline-flex items-center gap-1.5 text-sm text-slate-700 bg-orange-50 border border-orange-100 px-3 py-1.5 rounded-full">
                        <CheckCircle className="w-3.5 h-3.5 text-orange-500" />
                        {item.trim()}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Location Card */}
              <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                <h4 className="text-sm font-bold text-slate-900 mb-3 uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>Location</h4>
                {[
                  { icon: 'map-pin', label: 'Address', value: property.address },
                  { icon: 'navigation', label: 'Landmark', value: property.landmark },
                  { icon: 'map', label: 'City', value: property.city },
                  { icon: 'map', label: 'District', value: property.district },
                  { icon: 'flag', label: 'State', value: property.state },
                ]
                  .filter(item => item.value)
                  .map((item, index, array) => (
                    <InfoRow key={index} icon={item.icon} label={item.label} value={item.value} hideBorder={index === array.length - 1} />
                  ))}
                {property.latitude && property.longitude && (
                  <div className="mt-3 pt-3 border-t border-slate-100">
                    <a
                      href={`https://www.google.com/maps/search/?api=1&query=${property.latitude},${property.longitude}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-2 text-sm text-indigo-600 hover:text-indigo-700 font-medium"
                    >
                      <MapPin className="w-4 h-4" /> View on Google Maps →
                    </a>
                  </div>
                )}
              </div>

              {/* Rent Management Section */}
              {isRent && (property.status === 'rented' || property.status === 'active') && (
                <RentManagement propertyId={property.id} isPG={isPG} onRefresh={onRefresh} />
              )}

              {/* Food Menu Images (PG) */}
              {isPG && foodMenuImagesList.length > 0 && (
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                  <h4 className="text-sm font-bold text-slate-900 mb-3 uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>Food Menu</h4>
                  <div className="grid grid-cols-2 gap-2">
                    {foodMenuImagesList.map((img, i) => (
                      <img
                        key={i}
                        src={resolveImageUrl(img, 'food_menu')}
                        alt={`Food menu ${i + 1}`}
                        className="w-full h-32 object-cover rounded-lg border border-slate-100"
                        onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = 'https://via.placeholder.com/400x300/e2e8f0/94a3b8?text=Menu';
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Video / YouTube link */}
              {property.yt_link && (
                <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
                  <h4 className="text-sm font-bold text-slate-900 mb-3 uppercase tracking-wide" style={{ fontFamily: 'Outfit, sans-serif' }}>Video Tour</h4>
                  <a href={property.yt_link} target="_blank" rel="noopener noreferrer" className="text-sm text-indigo-600 hover:underline font-medium">
                    ▶️ Watch on YouTube
                  </a>
                </div>
              )}
            </div>
          )}

          {isNotesMode && (
            <div className="border-t border-slate-200 pt-4">
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-3">Notes Timeline</p>
              <div className="mb-4">
                <Textarea
                  value={newNote}
                  onChange={(event) => setNewNote(event.target.value)}
                  placeholder="Add a note..."
                  className="mb-2 bg-white min-h-[100px]"
                  data-testid="add-note-textarea"
                />
                <Button
                  onClick={addNote}
                  size="sm"
                  className="bg-indigo-600 hover:bg-indigo-700"
                  data-testid="add-note-button"
                >
                  Add Note
                </Button>
              </div>

              <div className="space-y-3 max-h-80 overflow-y-auto">
                {notes.map((note) => (
                  <div key={note.id} className="p-3 bg-white border border-slate-200 rounded-lg shadow-sm">
                    <div className="flex items-start justify-between mb-1">
                      <span className="text-sm font-medium text-slate-900">{note.user_name}</span>
                      <span className="text-xs text-slate-500">{new Date(note.created_at).toLocaleDateString()}</span>
                    </div>
                    <p className="text-sm text-slate-700 whitespace-pre-wrap">{note.note}</p>
                  </div>
                ))}
                {notes.length === 0 && (
                  <p className="text-sm text-slate-500 text-center py-4">No notes yet</p>
                )}
              </div>
            </div>
          )}

          {isStatusMode && (
            <div className="border-t border-slate-200 pt-4 space-y-4">
              <div>
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Status</p>
                <Select
                  options={[
                    { value: 'active', label: 'Active' },
                    { value: 'inactive', label: 'Inactive' },
                    { value: 'sold', label: 'Sold' },
                    { value: 'rented', label: 'Rented' }
                  ]}
                  value={status}
                  onChange={setStatus}
                  disabled={isUpdatingStatus}
                />
              </div>

              {status === 'sold' && (
                <div>
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Sell To Lead</p>
                  <Select
                    options={[
                      { value: 'sold_by_owner', label: 'Sold by the owner' },
                      ...availableLeads.map((lead) => ({
                        value: lead.id,
                        label: `${lead.buyer_name} (${lead.buyer_phone})`
                      }))
                    ]}
                    value={selectedLeadId}
                    onChange={setSelectedLeadId}
                    placeholder="Select lead or owner option"
                  />
                </div>
              )}

              <Button
                onClick={handleStatusUpdate}
                disabled={isUpdatingStatus || (status === 'sold' && !selectedLeadId)}
                className="mt-3 bg-indigo-600 hover:bg-indigo-700 w-full"
              >
                {isUpdatingStatus ? 'Updating...' : 'Update Status'}
              </Button>
            </div>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
};

export default PropertyDetail;
